import React, { useState, useEffect } from 'react';
import { Plus, Minus, Check, X, RefreshCw, Palette, Grid, AlertTriangle } from 'lucide-react';
import _ from 'lodash';

const GrannySquarePlanner = () => {
  // State management
  const [colors, setColors] = useState(['#FF5252', '#4CAF50', '#2196F3']);
  const [dimensions, setDimensions] = useState({ rows: 5, cols: 5 });
  const [blanketLayout, setBlanketLayout] = useState([]);
  const [completedSquares, setCompletedSquares] = useState([]);
  const [currentSquareIndex, setCurrentSquareIndex] = useState(0);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeColorIndex, setActiveColorIndex] = useState(null);
  const [colorPickerColor, setColorPickerColor] = useState('#FF5252');
  const [showAddColorPrompt, setShowAddColorPrompt] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // For showing save status messages

  // Load saved data when component mounts
  useEffect(() => {
    // Helper function to safely parse JSON with a fallback
    const safelyParseJSON = (json, fallback) => {
      try {
        const parsed = JSON.parse(json);
        return parsed || fallback;
      } catch (e) {
        return fallback;
      }
    };

    // Load dark mode preference
    const savedDarkMode = localStorage.getItem('grannySquareDarkMode');
    if (savedDarkMode) setIsDarkMode(savedDarkMode === 'true');
    
    // Load saved colors
    const savedColors = localStorage.getItem('grannySquareColors');
    if (savedColors) {
      const parsedColors = safelyParseJSON(savedColors, ['#FF5252', '#4CAF50', '#2196F3']);
      setColors(parsedColors);
    }
    
    // Load saved dimensions
    const savedDimensions = localStorage.getItem('grannySquareDimensions');
    if (savedDimensions) {
      const parsedDimensions = safelyParseJSON(savedDimensions, { rows: 5, cols: 5 });
      setDimensions(parsedDimensions);
    }
    
    // Load saved layout
    const savedLayout = localStorage.getItem('grannySquareBlanketLayout');
    if (savedLayout) {
      const parsedLayout = safelyParseJSON(savedLayout, []);
      setBlanketLayout(parsedLayout);
    }
    
    // Load completed squares
    const savedCompletedSquares = localStorage.getItem('grannySquareCompletedSquares');
    if (savedCompletedSquares) {
      const parsedCompletedSquares = safelyParseJSON(savedCompletedSquares, []);
      setCompletedSquares(parsedCompletedSquares);
    }
    
    // Load current square index
    const savedCurrentSquareIndex = localStorage.getItem('grannySquareCurrentIndex');
    if (savedCurrentSquareIndex) {
      const parsedIndex = parseInt(savedCurrentSquareIndex, 10);
      if (!isNaN(parsedIndex)) {
        setCurrentSquareIndex(parsedIndex);
      }
    }
    
    setSaveStatus('Loaded saved project');
    setTimeout(() => setSaveStatus(''), 2000);
  }, []);
  
  // Save data to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('grannySquareDarkMode', isDarkMode);
  }, [isDarkMode]);
  
  useEffect(() => {
    localStorage.setItem('grannySquareColors', JSON.stringify(colors));
    setSaveStatus('Saved colors');
    setTimeout(() => setSaveStatus(''), 2000);
  }, [colors]);
  
  useEffect(() => {
    localStorage.setItem('grannySquareDimensions', JSON.stringify(dimensions));
    setSaveStatus('Saved dimensions');
    setTimeout(() => setSaveStatus(''), 2000);
  }, [dimensions]);
  
  useEffect(() => {
    if (blanketLayout.length > 0) {
      localStorage.setItem('grannySquareBlanketLayout', JSON.stringify(blanketLayout));
      setSaveStatus('Saved layout');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  }, [blanketLayout]);
  
  useEffect(() => {
    if (completedSquares.length > 0) {
      localStorage.setItem('grannySquareCompletedSquares', JSON.stringify(completedSquares));
      setSaveStatus('Saved progress');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  }, [completedSquares]);
  
  useEffect(() => {
    localStorage.setItem('grannySquareCurrentIndex', currentSquareIndex.toString());
  }, [currentSquareIndex]);

  // Generate blanket layout when colors or dimensions change (but only if there isn't already a saved layout)
  useEffect(() => {
    if (colors.length >= 3 && blanketLayout.length === 0) {
      generateBlanketLayout();
    }
  }, [colors, dimensions]);

  // Regenerate layout when colors change to ensure all colors are used
  useEffect(() => {
    if (colors.length >= 3 && blanketLayout.length > 0) {
      // Check if current layout uses different number of colors than currently selected
      const firstSquare = blanketLayout[0];
      const currentLayerCount = Object.keys(firstSquare).length;
      
      if (currentLayerCount !== colors.length) {
        generateBlanketLayout();
      }
    }
  }, [colors.length]);

  // Initialize completed squares array
  useEffect(() => {
    if (blanketLayout.length > 0 && completedSquares.length === 0) {
      setCompletedSquares(Array(dimensions.rows * dimensions.cols).fill(false));
    } else if (blanketLayout.length > 0 && completedSquares.length !== dimensions.rows * dimensions.cols) {
      // Resize the completed squares array if dimensions changed
      setCompletedSquares(prev => {
        const newArray = Array(dimensions.rows * dimensions.cols).fill(false);
        // Copy over existing completion states where possible
        prev.forEach((value, index) => {
          if (index < newArray.length) {
            newArray[index] = value;
          }
        });
        return newArray;
      });
    }
  }, [blanketLayout, dimensions]);

  // Generate blanket layout ensuring each square uses each color once
  const generateBlanketLayout = () => {
    const totalSquares = dimensions.rows * dimensions.cols;
    const layout = [];
    
    // Generate all possible permutations of color positions
    // Each square uses each color exactly once (in different positions)
    for (let i = 0; i < totalSquares; i++) {
      // Create a permutation of the colors array for each square
      // This ensures each square uses all colors once but in different positions
      const permutation = _.shuffle([...colors]);
      
      // Create an object with dynamic layer names based on color count
      const squareData = {};
      
      // Add colors from innermost to outermost
      permutation.forEach((color, index) => {
        if (index === 0) {
          squareData.center = color;
        } else if (index === permutation.length - 1) {
          squareData.outer = color;
        } else {
          squareData[`layer${index}`] = color;
        }
      });
      
      layout.push(squareData);
    }
    
    setBlanketLayout(layout);
  };

  // Handle color add/remove
  const handleAddColor = () => {
    if (colors.length < 10) {
      setShowAddColorPrompt(true);
    }
  };

  const confirmAddColor = () => {
    setColors([...colors, colorPickerColor]);
    setShowAddColorPrompt(false);
  };

  const handleRemoveColor = (indexToRemove) => {
    if (colors.length > 3) {
      setColors(colors.filter((_, index) => index !== indexToRemove));
    }
  };

  // Handle color change
  const handleColorChange = (index, color) => {
    const newColors = [...colors];
    newColors[index] = color;
    setColors(newColors);
    setShowColorPicker(false);
  };

  // Handle dimension changes
  const handleDimensionChange = (dimension, delta) => {
    const newValue = dimensions[dimension] + delta;
    if (newValue >= 1 && newValue <= 20) {
      setDimensions(prev => ({ ...prev, [dimension]: newValue }));
    }
  };

  // Toggle completion status of a square
  const toggleSquareCompletion = (index) => {
    const newCompletedSquares = [...completedSquares];
    newCompletedSquares[index] = !newCompletedSquares[index];
    setCompletedSquares(newCompletedSquares);
  };

  // Set current square
  const setCurrentSquare = (index) => {
    setCurrentSquareIndex(index);
  };

  // Calculate progress statistics
  const calculateProgress = () => {
    const totalSquares = dimensions.rows * dimensions.cols;
    const completedCount = completedSquares.filter(status => status).length;
    const percentComplete = Math.round((completedCount / totalSquares) * 100);
    
    return {
      total: totalSquares,
      completed: completedCount,
      remaining: totalSquares - completedCount,
      percent: percentComplete
    };
  };

  // Simple Color Picker component
  const ColorPicker = ({ onChange, initialColor }) => {
    const [currentColor, setCurrentColor] = useState(initialColor || '#ff0000');
    
    const handleColorChange = (e) => {
      const newColor = e.target.value;
      setCurrentColor(newColor);
      if (onChange) onChange(newColor);
    };
    
    // Predefined color palette
    const colorPalette = [
      '#FF5252', '#E91E63', '#9C27B0', '#673AB7', 
      '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', 
      '#009688', '#4CAF50', '#8BC34A', '#CDDC39',
      '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
      '#795548', '#9E9E9E', '#607D8B', '#FFFFFF',
      '#000000', '#FFC0CB', '#FFD700', '#800080'
    ];

    return (
      <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pick a Color</label>
          <input
            type="color"
            value={currentColor}
            onChange={handleColorChange}
            className="w-full h-12 cursor-pointer rounded border border-gray-300 dark:border-gray-600"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Quick Colors</label>
          <div className="grid grid-cols-6 gap-2">
            {colorPalette.map((color, index) => (
              <button
                key={index}
                className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                style={{ backgroundColor: color }}
                onClick={() => {
                  setCurrentColor(color);
                  if (onChange) onChange(color);
                }}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
        </div>
        
        <div className="mt-4 flex items-center">
          <div 
            className="w-12 h-12 rounded-full border border-gray-300 dark:border-gray-600 mr-3"
            style={{ backgroundColor: currentColor }}
          ></div>
          <div className="text-gray-800 dark:text-gray-200 font-mono">
            {currentColor.toUpperCase()}
          </div>
        </div>
      </div>
    );
  };

  // Handle color picker change
  const handleColorPickerChange = (color) => {
    setColorPickerColor(color);
  };

  // Render color picker modal
  const renderColorPicker = () => {
    if (!showColorPicker && !showAddColorPrompt) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              {showAddColorPrompt ? 'Add New Color' : 'Edit Color'}
            </h3>
            <button 
              onClick={() => {
                setShowColorPicker(false);
                setShowAddColorPrompt(false);
              }}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6">
            <ColorPicker 
              onChange={handleColorPickerChange} 
              initialColor={showAddColorPrompt ? '#ff0000' : colors[activeColorIndex]}
            />
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowColorPicker(false);
                  setShowAddColorPrompt(false);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (showAddColorPrompt) {
                    confirmAddColor();
                  } else {
                    handleColorChange(activeColorIndex, colorPickerColor);
                  }
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                {showAddColorPrompt ? 'Add Color' : 'Update Color'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render granny square
  const renderGrannySquare = (combination, size = 'md', completed = false) => {
    const sizeClasses = {
      sm: 'w-12 h-12',
      md: 'w-16 h-16 md:w-20 md:h-20',
      lg: 'w-32 h-32 md:w-40 md:h-40'
    };
    
    // Get total number of layers
    const totalLayers = Object.keys(combination).length;
    
    // Calculate the total width/height available based on size
    const getTotalSize = () => {
      if (size === 'sm') return 12 * 4; // w-12 = 3rem = 48px
      if (size === 'md') return 20 * 4; // w-20 = 5rem = 80px
      return 40 * 4; // w-40 = 10rem = 160px
    };
    
    // Each layer should have equal thickness
    const layerThickness = getTotalSize() / (totalLayers * 2);
    
    return (
      <div 
        className={`relative ${sizeClasses[size]} rounded-lg shadow-md overflow-hidden transition-transform transform hover:scale-105 ${completed ? 'ring-2 ring-green-500 ring-offset-2' : ''}`}
      >
        {/* Render all layers from outer to inner */}
        {Object.entries(combination).sort((a, b) => {
          // Sort to ensure outer is first, center is last
          if (a[0] === 'outer') return -1;
          if (b[0] === 'outer') return 1;
          if (a[0] === 'center') return 1;
          if (b[0] === 'center') return -1;
          return a[0].localeCompare(b[0]);
        }).map(([layerName, color], index) => {
          // Calculate inset for this layer - each layer is pushed in by the 
          // thickness of all previous layers
          const insetValue = index * layerThickness;
          return (
            <div 
              key={layerName}
              className="absolute"
              style={{ 
                backgroundColor: color,
                top: insetValue,
                right: insetValue,
                bottom: insetValue,
                left: insetValue
              }}
            ></div>
          );
        })}
        
        {/* Completed checkmark */}
        {completed && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
            <Check className="text-white" size={32} />
          </div>
        )}
      </div>
    );
  };

  // Calculate current square details
  const getCurrentSquareDetails = () => {
    if (!blanketLayout[currentSquareIndex]) return null;
    
    return {
      index: currentSquareIndex,
      row: Math.floor(currentSquareIndex / dimensions.cols) + 1,
      col: (currentSquareIndex % dimensions.cols) + 1,
      colors: blanketLayout[currentSquareIndex],
      completed: completedSquares[currentSquareIndex] || false
    };
  };

  const progress = calculateProgress();
  const currentSquare = getCurrentSquareDetails();

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'} transition-colors duration-300`}>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <header className="mb-8 flex flex-col md:flex-row justify-between items-center">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-indigo-600 dark:text-indigo-400">Granny Square Planner</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Design, visualize, and track your crochet blanket project</p>
            {saveStatus && (
              <div className="mt-2 text-sm text-green-600 dark:text-green-400 animate-pulse transition-opacity">
                {saveStatus}
              </div>
            )}
          </div>
          <div className="mt-4 md:mt-0 flex gap-4 items-center">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Auto-saves to browser
            </div>
            <button 
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? '🔆' : '🌙'}
            </button>
          </div>
        </header>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left column - Configuration & Current Square */}
          <div className="lg:col-span-4 space-y-6">
            {/* Configuration Section */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 transition-all">
              <div className="flex items-center mb-4">
                <Palette className="text-indigo-500 mr-2" size={20} />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Color Palette</h2>
              </div>
              
              {/* Color Swatches */}
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {colors.map((color, index) => (
                    <div key={index} className="group relative">
                      <button
                        className="w-10 h-10 rounded-full shadow-md border-2 border-white dark:border-gray-700 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        onClick={() => {
                          setActiveColorIndex(index);
                          setColorPickerColor(color);
                          setShowColorPicker(true);
                        }}
                        aria-label={`Edit color ${index + 1}`}
                      />
                      {colors.length > 3 && (
                        <button
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleRemoveColor(index)}
                          aria-label={`Remove color ${index + 1}`}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  
                  {colors.length < 10 && (
                    <button
                      className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      onClick={handleAddColor}
                      aria-label="Add new color"
                    >
                      <Plus size={20} />
                    </button>
                  )}
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Each square will have as many layers as there are colors ({colors.length} layers)
                </p>
                {colors.length > 0 && (
                  <div className="mt-1 flex items-center">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Colors:</span>
                    {colors.length} / 10 max
                  </div>
                )}
              </div>
              
              {/* Blanket Dimensions */}
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <Grid className="text-indigo-500 mr-2" size={16} />
                  <h3 className="font-medium text-gray-700 dark:text-gray-300">Blanket Size</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Rows</div>
                    <div className="flex items-center">
                      <button
                        className="p-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                        onClick={() => handleDimensionChange('rows', -1)}
                        disabled={dimensions.rows <= 1}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="mx-3 font-bold text-lg text-gray-800 dark:text-gray-100">{dimensions.rows}</span>
                      <button
                        className="p-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                        onClick={() => handleDimensionChange('rows', 1)}
                        disabled={dimensions.rows >= 20}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Columns</div>
                    <div className="flex items-center">
                      <button
                        className="p-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                        onClick={() => handleDimensionChange('cols', -1)}
                        disabled={dimensions.cols <= 1}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="mx-3 font-bold text-lg text-gray-800 dark:text-gray-100">{dimensions.cols}</span>
                      <button
                        className="p-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                        onClick={() => handleDimensionChange('cols', 1)}
                        disabled={dimensions.cols >= 20}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Generate and Reset Buttons */}
              <div className="flex gap-2">
                <button
                  className="flex-1 py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow transition-colors flex items-center justify-center"
                  onClick={generateBlanketLayout}
                >
                  <RefreshCw size={16} className="mr-2" />
                  Regenerate Layout
                </button>
                
                <button
                  className="py-2 px-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg shadow transition-colors"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to reset all data? This cannot be undone.')) {
                      localStorage.clear();
                      setColors(['#FF5252', '#4CAF50', '#2196F3']);
                      setDimensions({ rows: 5, cols: 5 });
                      setBlanketLayout([]);
                      setCompletedSquares([]);
                      setCurrentSquareIndex(0);
                      setSaveStatus('Project reset');
                      setTimeout(() => setSaveStatus(''), 2000);
                    }
                  }}
                  title="Reset all data"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Current Square */}
            {currentSquare && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 transition-all">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Current Square</h2>
                <div className="flex flex-col items-center">
                  <div className="mb-4">
                    {renderGrannySquare(currentSquare.colors, 'lg', currentSquare.completed)}
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Square #{currentSquare.index + 1}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      Row {currentSquare.row}, Column {currentSquare.col}
                    </p>
                  </div>
                  
                  <div className="w-full space-y-3 mb-4">
                    {/* Dynamically render layer information */}
                    {Object.entries(currentSquare.colors).sort((a, b) => {
                      // Sort to ensure center is first, then layer1, layer2, etc., and outer is last
                      if (a[0] === 'center') return -1;
                      if (b[0] === 'center') return 1;
                      if (a[0] === 'outer') return 1;
                      if (b[0] === 'outer') return -1;
                      return a[0].localeCompare(b[0]);
                    }).map(([layerName, color]) => {
                      // Format layer name for display
                      let displayName;
                      if (layerName === 'center') {
                        displayName = 'Center';
                      } else if (layerName === 'outer') {
                        displayName = 'Outer Row';
                      } else {
                        // Convert layerX to "Row X"
                        const layerNum = layerName.replace('layer', '');
                        displayName = `Row ${layerNum}`;
                      }
                      
                      return (
                        <div key={layerName} className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center">
                            <div 
                              className="w-6 h-6 rounded-full mr-2"
                              style={{ backgroundColor: color }}
                            ></div>
                            <span className="text-gray-700 dark:text-gray-300">{displayName}</span>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Color {colors.indexOf(color) + 1}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  
                  <button
                    className={`w-full py-2 px-4 rounded-lg shadow flex items-center justify-center transition-colors ${
                      currentSquare.completed 
                        ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                    onClick={() => toggleSquareCompletion(currentSquare.index)}
                  >
                    {currentSquare.completed ? (
                      <>
                        <X size={16} className="mr-2" />
                        Mark as Incomplete
                      </>
                    ) : (
                      <>
                        <Check size={16} className="mr-2" />
                        Mark as Complete
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Right column - Progress & Blanket Overview */}
          <div className="lg:col-span-8 space-y-6">
            {/* Progress Overview */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 transition-all">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Progress Overview</h2>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-indigo-50 dark:bg-indigo-900 dark:bg-opacity-30 p-4 rounded-lg text-center">
                  <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Total</div>
                  <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{progress.total}</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900 dark:bg-opacity-30 p-4 rounded-lg text-center">
                  <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Completed</div>
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">{progress.completed}</div>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900 dark:bg-opacity-30 p-4 rounded-lg text-center">
                  <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Remaining</div>
                  <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">{progress.remaining}</div>
                </div>
              </div>
              <div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all duration-500 ease-in-out"
                    style={{ width: `${progress.percent}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>0%</span>
                  <span>{progress.percent}% Complete</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
            
            {/* Blanket Overview */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 transition-all">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Blanket Overview</h2>
              {blanketLayout.length > 0 ? (
                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-auto">
                  <div 
                    className="grid gap-3 mx-auto"
                    style={{ 
                      gridTemplateColumns: `repeat(${dimensions.cols}, minmax(0, 1fr))`, 
                      maxWidth: `${Math.min(dimensions.cols * 90, 1000)}px` 
                    }}
                  >
                    {blanketLayout.map((combination, index) => (
                      <button
                        key={index}
                        className={`cursor-pointer transition-all duration-200 ${
                          currentSquareIndex === index 
                            ? 'ring-4 ring-indigo-500 ring-opacity-75 z-10 transform scale-105' 
                            : 'hover:ring-2 hover:ring-indigo-300 dark:hover:ring-indigo-600'
                        }`}
                        onClick={() => setCurrentSquare(index)}
                      >
                        {renderGrannySquare(combination, 'md', completedSquares[index])}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 dark:bg-yellow-900 dark:bg-opacity-30 p-4 rounded-lg flex items-center text-yellow-800 dark:text-yellow-300">
                  <AlertTriangle size={20} className="mr-2" />
                  <span>Select at least 3 colors to generate your blanket layout. Each color will create a layer in your granny squares.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Color Picker Modal */}
      {renderColorPicker()}
    </div>
  );
};

export default GrannySquarePlanner;